import { DisposableBase, type IInitializable } from '../../Types';
import { action, observable } from 'mobx';
import { Api, type AuthenticateModel, type AuthenticateResult } from '../../Clients/Identities/Api';
import { makePersistable } from 'mobx-persist-store';
import { readConfigFromUrl, applyUrlConfig, generateConfigUrl } from '../../Utils/UrlConfigUtils';
import { debugLog } from '../../Stores/DebugLogStore';

/** Default Identities entity-field names mapped onto a Guard Mode person card. */
const GUARD_FIELD_DEFAULTS = {
    vendor: 'EMPLOYER',
    email: 'EMAIL',
    phone: 'PHONE_NUMBER',
    dob: 'BIRTHDAY',
} as const;

export class IdentitiesSetupStore extends DisposableBase {
    @observable accessor serverAddress: string = '';

    @action setServerAddress = (value: string) => {
        this.serverAddress = value;
    };

    @observable accessor username: string = '';

    @action setUsername = (value: string) => {
        this.username = value;
    };

    @observable accessor password: string = '';

    @action setPassword = (value: string) => {
        this.password = value;
    };

    @observable accessor isConnecting: boolean = false;
    @observable accessor connectionError: string | null = null;
    @observable accessor isConnected: boolean = false;
    @observable accessor authToken: string | null = null;
    @observable accessor tokenTimestamp: number | null = null; // Timestamp when token was acquired

    @action setConnecting = (value: boolean) => {
        this.isConnecting = value;
    };

    @action setConnectionError = (error: string | null) => {
        this.connectionError = error;
    };

    @action setConnected = (value: boolean) => {
        this.isConnected = value;
    };

    @action setAuthToken = (token: string | null) => {
        this.authToken = token;
        this.tokenTimestamp = token ? Date.now() : null;
    };

    @observable accessor isAutoConnecting: boolean = false;

    @action setAutoConnecting = (value: boolean) => {
        this.isAutoConnecting = value;
    };

    @action stopAutoConnect = () => {
        this.setAutoConnecting(false);
    };

    constructor() {
        super();
        // Configure persistence for this store instance
        makePersistable(this, {
            name: 'IdentitiesSetupStore',
            properties: ['serverAddress', 'username', 'password', 'authToken', 'tokenTimestamp'],
            storage: localStorage,
        }).catch(console.error);
    }

    @action connect = async () => {
        if (!this.serverAddress.trim() || !this.username.trim() || !this.password.trim()) {
            this.setConnectionError('Please fill in all fields');
            return;
        }

        this.setConnecting(true);
        this.setConnectionError(null);

        try {
            const api = new Api({
                baseUrl: this.serverAddress.trim(),
            });

            const authenticateData: AuthenticateModel = {
                username: this.username.trim(),
                password: this.password.trim(),
            };

            const result: AuthenticateResult = await api.api.authenticate(authenticateData);

            if (result.token) {
                debugLog.info(`Identities server authenticated successfully`);
                this.setAuthToken(result.token);
                this.setConnected(true);
                this.setConnectionError(null);
            } else {
                debugLog.error('Authentication failed: no token received');
                this.setConnectionError('Authentication failed: No token received');
                this.setConnected(false);
            }
        } catch (error) {
            debugLog.error(`Identities auth failed: ${error instanceof Error ? error.message : String(error)}`);
            console.error('Authentication failed:', error);
            this.setConnectionError(error instanceof Error ? error.message : 'Connection failed');
            this.setConnected(false);
        } finally {
            this.setConnecting(false);
        }
    };

    @action autoConnect = async () => {
        console.log('IdentitiesSetupStore: autoConnect called');
        if (this.isAutoConnecting || this.isConnected) return;

        if (!this.serverAddress.trim() || !this.username.trim() || !this.password.trim()) {
            return; // Don't auto-connect if credentials are not set
        }
        this.setAutoConnecting(true);

        while (this.isAutoConnecting && !this.isConnected) {
            await this.connect();
            if (!this.isConnected) {
                // Wait 5 seconds before retrying
                await new Promise((resolve) => setTimeout(resolve, 5000));
            }
        }

        this.setAutoConnecting(false);
    };

    /**
     * Checks if the current token is expired (older than 2 hours)
     */
    private isTokenExpired(): boolean {
        if (!this.tokenTimestamp) {
            return true;
        }
        const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
        const elapsed = Date.now() - this.tokenTimestamp;
        return elapsed >= TWO_HOURS_MS;
    }

    /**
     * Gets the latest valid token. If token is expired, attempts to re-authenticate.
     * Returns null if re-authentication fails.
     */
    async getLatestToken(): Promise<string | null> {
        // If we have a valid token that's not expired, return it
        if (this.authToken && !this.isTokenExpired()) {
            return this.authToken;
        }

        // Token is expired or doesn't exist, try to re-authenticate
        console.log('Token expired or missing, attempting to re-authenticate...');
        
        try {
            await this.connect();
            
            if (this.isConnected && this.authToken) {
                console.log('Re-authentication successful');
                return this.authToken;
            } else {
                console.error('Re-authentication failed');
                return null;
            }
        } catch (error) {
            console.error('Error during re-authentication:', error);
            return null;
        }
    }

    @action reset = () => {
        this.serverAddress = '';
        this.username = '';
        this.password = '';
        this.isConnecting = false;
        this.connectionError = null;
        this.isConnected = false;
        this.authToken = null;
        this.tokenTimestamp = null;
    };
}

export class SettingsStore extends DisposableBase implements IInitializable {
    @observable accessor identitiesSetup = new IdentitiesSetupStore();

    @observable accessor showNerdStatistics: boolean = false;

    @action setShowNerdStatistics = (value: boolean) => {
        this.showNerdStatistics = value;
    };

    @observable accessor showDebugLog: boolean = false;

    @action setShowDebugLog = (value: boolean) => {
        this.showDebugLog = value;
    };

    @observable accessor faceSpotlight: boolean = false;

    @action setFaceSpotlight = (value: boolean) => {
        this.faceSpotlight = value;
    };

    @observable accessor maxFaceCaptureDurationMs: number = 1_000;

    @action setMaxFaceCaptureDurationMs = (value: number) => {
        this.maxFaceCaptureDurationMs = value;
    };

    @observable accessor faceDebouceDurationMs: number = 6_000;

    @action setFaceDebouceDurationMs = (value: number) => {
        this.faceDebouceDurationMs = value;
    };

    @observable accessor faceIdealQuality: number = 0.75;

    @action setFaceIdealQuality = (value: number) => {
        this.faceIdealQuality = value;
    };

    @observable accessor minFaceSizePercent: number = 12;

    @action setMinFaceSizePercent = (value: number) => {
        this.minFaceSizePercent = value;
    };

    @observable accessor minFaceQuality: number = 0.5;

    @action setMinFaceQuality = (value: number) => {
        this.minFaceQuality = value;
    };

    @observable accessor minFrontality: number = 0.6;

    @action setMinFrontality = (value: number) => {
        this.minFrontality = value;
    };

    @observable accessor detectorName: string = 'eConnect Face Web';

    @action setDetectorName = (value: string) => {
        this.detectorName = value;
    };

    @observable accessor authPin: string = '0012';

    @action setAuthPin = (value: string) => {
        this.authPin = value;
    };

    @observable accessor flipVideoHorizontally: boolean = true;

    @action setFlipVideoHorizontally = (value: boolean) => {
        this.flipVideoHorizontally = value;
    };

    /**
     * The horizontal flip ACTUALLY applied to both the rendered video and the
     * face-overlay canvas. Guard Mode uses the rear (environment) camera, which
     * must never be mirrored — so guard always returns false regardless of the
     * user's flipVideoHorizontally preference. Kiosk returns the preference as-is.
     * Single source of truth: video + overlay must use this, never the raw flag.
     */
    get effectiveFlipHorizontally(): boolean {
        return this.appMode === 'guard' ? false : this.flipVideoHorizontally;
    }

    @observable accessor faceExpansionFactor: number = 2.5;

    @action setFaceExpansionFactor = (value: number) => {
        // Clamp to reasonable bounds to prevent issues
        this.faceExpansionFactor = Math.max(1.0, Math.min(5.0, value));
    };

    @observable accessor eventThemeDisplayDurationMs: number = 4000;

    @action setEventThemeDisplayDurationMs = (value: number) => {
        this.eventThemeDisplayDurationMs = value;
    };

    @observable accessor preferredCameraLabel: string | null = null;

    @action setPreferredCameraLabel = (value: string | null) => {
        this.preferredCameraLabel = value;
    };

    @observable accessor minLaplacian: number = 2000;

    @action setMinLaplacian = (value: number) => {
        this.minLaplacian = value;
    };

    @observable accessor appMode: 'kiosk' | 'guard' = 'kiosk';
    @action setAppMode = (value: 'kiosk' | 'guard') => { this.appMode = value; };

    @observable accessor guardNeutralDetector: string = '';
    @action setGuardNeutralDetector = (value: string) => { this.guardNeutralDetector = value; };

    @observable accessor guardInDetectors: string[] = [];
    @action setGuardInDetectors = (value: string[]) => { this.guardInDetectors = value; };

    @observable accessor guardOutDetectors: string[] = [];
    @action setGuardOutDetectors = (value: string[]) => { this.guardOutDetectors = value; };

    @observable accessor guardSoundEnabled: boolean = true;
    @action setGuardSoundEnabled = (value: boolean) => { this.guardSoundEnabled = value; };

    /**
     * Offers an Ignore button next to In/Out on granted results. When off, the
     * guard must record In or Out — but Dismiss still appears whenever no
     * In/Out button is available, so a result can always be dismissed.
     */
    @observable accessor guardShowIgnoreButton: boolean = true;
    @action setGuardShowIgnoreButton = (value: boolean) => { this.guardShowIgnoreButton = value; };

    @observable accessor guardVendorField: string = GUARD_FIELD_DEFAULTS.vendor;
    @action setGuardVendorField = (value: string) => { this.guardVendorField = value; };

    @observable accessor guardEmailField: string = GUARD_FIELD_DEFAULTS.email;
    @action setGuardEmailField = (value: string) => { this.guardEmailField = value; };

    @observable accessor guardPhoneField: string = GUARD_FIELD_DEFAULTS.phone;
    @action setGuardPhoneField = (value: string) => { this.guardPhoneField = value; };

    @observable accessor guardDobField: string = GUARD_FIELD_DEFAULTS.dob;
    @action setGuardDobField = (value: string) => { this.guardDobField = value; };

    /** Resets only the person field-mapping names to their defaults. */
    @action resetGuardFieldMapping = () => {
        this.guardVendorField = GUARD_FIELD_DEFAULTS.vendor;
        this.guardEmailField = GUARD_FIELD_DEFAULTS.email;
        this.guardPhoneField = GUARD_FIELD_DEFAULTS.phone;
        this.guardDobField = GUARD_FIELD_DEFAULTS.dob;
    };

    /**
     * Resets all settings to their default values
     */
    @action resetToDefaults = (): void => {
        console.log('Resetting all settings to default values');

        // Reset main settings to defaults
        this.showNerdStatistics = false;
        this.showDebugLog = false;
        this.faceSpotlight = false;
        this.maxFaceCaptureDurationMs = 1_000;
        this.faceDebouceDurationMs = 3_000;
        this.faceIdealQuality = 0.75;
        this.minFaceSizePercent = 12;
        this.minFaceQuality = 0.5;
        this.minFrontality = 0.6;
        this.detectorName = 'eConnect Face Web';
        this.authPin = '0012';
        this.flipVideoHorizontally = false;
        this.faceExpansionFactor = 2.5;
        this.eventThemeDisplayDurationMs = 4000;
        this.preferredCameraLabel = null;
        this.minLaplacian = 2000;
        this.appMode = 'kiosk';
        this.guardNeutralDetector = '';
        this.guardInDetectors = [];
        this.guardOutDetectors = [];
        this.guardSoundEnabled = true;
        this.guardShowIgnoreButton = true;
        this.guardVendorField = GUARD_FIELD_DEFAULTS.vendor;
        this.guardEmailField = GUARD_FIELD_DEFAULTS.email;
        this.guardPhoneField = GUARD_FIELD_DEFAULTS.phone;
        this.guardDobField = GUARD_FIELD_DEFAULTS.dob;

        // Reset identities setup
        this.identitiesSetup.reset();
    };

    /**
     * Loads configuration from URL parameters (compressed 'c' parameter or uncompressed 'config' parameter)
     * This allows sharing configurations via URL, or resetting to defaults with empty parameter
     * After processing, the URL parameters are removed from the browser address bar
     */
    @action loadConfigurationFromUrl = (eventThemesStore?: any): boolean => {
        try {
            const urlConfig = readConfigFromUrl();
            if (!urlConfig) {
                console.log('No URL configuration found');
                return false;
            }

            let configApplied = false;

            // Handle reset signal
            if (urlConfig === 'RESET') {
                console.log('URL configuration indicates reset to defaults');
                this.resetToDefaults();
                // Also reset event themes to defaults if store is provided
                if (eventThemesStore && eventThemesStore.resetToDefaults) {
                    eventThemesStore.resetToDefaults();
                }
                configApplied = true;
            } else {
                console.log('Found URL configuration:', urlConfig);

                // Apply configuration to this settings store (excluding complex objects)
                const applied = applyUrlConfig(this, urlConfig);

                if (applied) {
                    console.log('Successfully applied URL configuration to settings');
                }

                // Separately handle identities setup configuration
                if (urlConfig.identitiesSetup || urlConfig.serverAddress || urlConfig.username || urlConfig.password) {
                    const identitiesConfig = urlConfig.identitiesSetup || {
                        serverAddress: urlConfig.serverAddress,
                        username: urlConfig.username,
                        password: urlConfig.password,
                    };

                    if (applyUrlConfig(this.identitiesSetup, identitiesConfig)) {
                        console.log('Successfully applied URL configuration to identities setup');
                    }
                }

                // Separately handle event themes configuration
                if (urlConfig.eventThemes && Array.isArray(urlConfig.eventThemes) && eventThemesStore) {
                    try {
                        console.log('Applying event themes from URL configuration');
                        eventThemesStore.replaceThemes(urlConfig.eventThemes);
                        console.log('Successfully applied URL configuration to event themes');
                        configApplied = true;
                    } catch (error) {
                        console.error('Failed to apply event themes from URL:', error);
                    }
                }

                configApplied = applied;
            }

            // Clean up URL parameters after successful configuration
            // if (configApplied) {
            //     this.cleanUrlParameters();
            // }

            return configApplied;
        } catch (error) {
            console.error('Error loading configuration from URL:', error);
            return false;
        }
    };

    /**
     * Removes configuration parameters from the browser URL without reloading the page
     */
    private cleanUrlParameters = (): void => {
        try {
            const url = new URL(window.location.href);
            const hasConfigParam = url.searchParams.has('c') || url.searchParams.has('config');

            if (hasConfigParam) {
                // Remove the configuration parameters
                url.searchParams.delete('c');
                url.searchParams.delete('config');

                // Update the browser URL without reloading the page
                const newUrl = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '');
                window.history.replaceState({}, '', newUrl);

                console.log('Cleaned URL parameters, new URL:', newUrl);
            }
        } catch (error) {
            console.error('Error cleaning URL parameters:', error);
        }
    };

    /**
     * Generates a shareable URL with current configuration
     */
    generateConfigUrl = (baseUrl?: string, eventThemesStore?: any): string => {
        const currentUrl = baseUrl || window.location.origin; //+ window.location.pathname;

        const config = {
            showNerdStatistics: this.showNerdStatistics,
            showDebugLog: this.showDebugLog,
            faceSpotlight: this.faceSpotlight,
            maxFaceCaptureDurationMs: this.maxFaceCaptureDurationMs,
            faceDebouceDurationMs: this.faceDebouceDurationMs,
            faceIdealQuality: this.faceIdealQuality,
            minFaceSizePercent: this.minFaceSizePercent,
            minFaceQuality: this.minFaceQuality,
            minFrontality: this.minFrontality,
            detectorName: this.detectorName,
            flipVideoHorizontally: this.flipVideoHorizontally,
            faceExpansionFactor: this.faceExpansionFactor,
            eventThemeDisplayDurationMs: this.eventThemeDisplayDurationMs,
            preferredCameraLabel: this.preferredCameraLabel,
            minLaplacian: this.minLaplacian,
            authPin: this.authPin,
            appMode: this.appMode,
            guardNeutralDetector: this.guardNeutralDetector,
            guardInDetectors: this.guardInDetectors,
            guardOutDetectors: this.guardOutDetectors,
            guardSoundEnabled: this.guardSoundEnabled,
            guardShowIgnoreButton: this.guardShowIgnoreButton,
            guardVendorField: this.guardVendorField,
            guardEmailField: this.guardEmailField,
            guardPhoneField: this.guardPhoneField,
            guardDobField: this.guardDobField,
            // Include identities setup if configured
            identitiesSetup: {
                serverAddress: this.identitiesSetup.serverAddress,
                username: this.identitiesSetup.username,
                password: this.identitiesSetup.password,
                // Note: Excluding password for security reasons
            },
            // Include event themes if provided
            eventThemes: eventThemesStore ? eventThemesStore.themes : undefined,
        };

        return generateConfigUrl(currentUrl, config);
    };

    async initialize(eventThemesStore?: any) {
        // Add any initialization logic here if needed

        // Configure persistence for this store instance
        await makePersistable(this, {
            name: 'SettingsStore',
            properties: [
                'showNerdStatistics',
                'showDebugLog',
                'faceSpotlight',
                'maxFaceCaptureDurationMs',
                'faceDebouceDurationMs',
                'faceIdealQuality',
                'minFaceSizePercent',
                'minFaceQuality',
                'minFrontality',
                'detectorName',
                'authPin',
                'flipVideoHorizontally',
                'faceExpansionFactor',
                'eventThemeDisplayDurationMs',
                'preferredCameraLabel',
                'minLaplacian',
                'appMode',
                'guardNeutralDetector',
                'guardInDetectors',
                'guardOutDetectors',
                'guardSoundEnabled',
                'guardShowIgnoreButton',
                'guardVendorField',
                'guardEmailField',
                'guardPhoneField',
                'guardDobField',
            ],
            storage: localStorage,
        }).catch(console.error);

        // Check for URL configuration parameters and apply them
        this.loadConfigurationFromUrl(eventThemesStore);

        // Start auto-connecting to identities
        await this.identitiesSetup.autoConnect();
    }

    dispose() {
        this.identitiesSetup.dispose();
        super.dispose();
    }
}
